import 'vue-sonner/style.css';
import './toaster.css';

import type { AllowedComponentProps, AppContext, Component, VNodeChild, VNodeProps } from 'vue';
import { getCurrentInstance, h, markRaw, render } from 'vue';
import { toast, Toaster } from 'vue-sonner';

import BaseNotification from '@/components/Ui/BaseNotification.vue';
import type { NotificationLayout, NotificationType } from '@/components/Ui/notificationProps';
import { NOTIFICATION_TYPES } from '@/components/Ui/notificationProps';

const DEFAULT_DURATION_MS = 5000;
const TOUCH_EXPANDED_IDLE_MS = 3000;

export type NotificationId = string | number;

export interface NotificationProps {
  type: NotificationType;
  title?: string;
  layout?: NotificationLayout;
  hideIcon?: boolean;
  isDismissible?: boolean;
}

interface NotificationBehaviour {
  /** In milliseconds; 0 keeps the toast up until it is dismissed. */
  duration?: number;
  onClose?: () => void;
}

type ComponentPropsOf<C> = C extends new (...args: any[]) => { $props: infer P }
  ? Omit<P, keyof VNodeProps | keyof AllowedComponentProps>
  : Record<string, unknown>;

interface TextNotificationOptions extends NotificationProps, NotificationBehaviour {
  text: string;
  content?: never;
  contentProps?: never;
}

interface ContentNotificationOptions<C extends Component> extends NotificationProps, NotificationBehaviour {
  content: C;
  contentProps?: ComponentPropsOf<C>;
  text?: never;
}

export type ShowNotificationOptions<C extends Component = Component> =
  | TextNotificationOptions
  | ContentNotificationOptions<C>;

export type NotificationShorthandOptions = Omit<TextNotificationOptions, 'type' | 'text' | 'content' | 'contentProps'>;

export type NotificationShorthand = (text: string, options?: NotificationShorthandOptions) => NotificationId;

export type NotificationShorthands = Record<NotificationType, NotificationShorthand>;

export interface ShowReactiveNotificationOptions extends NotificationBehaviour {
  getNotificationProps: () => NotificationProps;
  renderContent: () => VNodeChild;
}

let isToasterMounted = false;

/**
 * vue-sonner expands the stack on mouseenter and keeps every timer paused until mouseleave. Touch has
 * no hover to end, and mobile browsers fire mouseleave unreliably (iOS Safari only when the next tap
 * lands on something clickable), so a tapped stack stayed expanded and never auto-closed. Replaying
 * mouseleave collapses it and resumes the timers: at once on a touch elsewhere or a scroll, otherwise
 * once the stack has gone untouched for a while.
 */
const collapseStackAfterTouch = (container: HTMLElement): void => {
  let collapseTimeoutId: number | undefined;
  let isTouchInput = false;

  const collapse = (): void => {
    isTouchInput = false;
    window.clearTimeout(collapseTimeoutId);
    container.querySelector('[data-sonner-toaster]')?.dispatchEvent(new MouseEvent('mouseleave'));
  };

  // Scroll is listened to on its own as well: a touch that starts a scroll is not always seen as a
  // pointerdown outside the stack.
  document.addEventListener('scroll', () => {
    if (isTouchInput) {
      collapse();
    }
  }, { capture: true, passive: true });

  document.addEventListener('pointerdown', (event) => {
    isTouchInput = event.pointerType === 'touch';

    if (!isTouchInput) {
      return;
    }

    if (event.target instanceof Node && container.contains(event.target)) {
      window.clearTimeout(collapseTimeoutId);
      collapseTimeoutId = window.setTimeout(collapse, TOUCH_EXPANDED_IDLE_MS);
    } else {
      collapse();
    }
  }, { capture: true, passive: true });
};

// Rendered with the caller's app context rather than as a separate app, so toast content keeps
// access to that app's global components, directives and provides.
const mountToaster = (appContext: AppContext | null): void => {
  if (isToasterMounted) {
    return;
  }

  const container = document.createElement('div');
  document.body.appendChild(container);

  const vnode = h(Toaster, {
    position: 'top-center',
    offset: { top: 40 },
    mobileOffset: { top: 50, left: 16, right: 16 },
    gap: 12,
    style: { '--width': '420px', zIndex: 1000000000 },
  });
  vnode.appContext = appContext;
  render(vnode, container);

  collapseStackAfterTouch(container);

  isToasterMounted = true;
};

const ToastNotification = markRaw({
  name: 'ToastNotification',
  inheritAttrs: false,
  props: {
    getNotificationProps: { type: Function, required: true },
    renderContent: { type: Function, required: true },
  },
  // vue-sonner hands custom toasts its close handler as this event; without it BaseNotification's
  // dismiss button would only hide the content and leave the toast in the stack.
  emits: ['closeToast'],
  setup(props: Pick<ShowReactiveNotificationOptions, 'getNotificationProps' | 'renderContent'>, { emit }) {
    return () => h(
      BaseNotification,
      { layout: 'inline', ...props.getNotificationProps(), onDismiss: () => emit('closeToast') },
      { default: () => props.renderContent() },
    );
  },
});

/**
 * Low-level variant: props are read through a getter and content through a render function, so both
 * stay reactive to the caller's state for as long as the toast is up.
 */
export const showReactiveNotification = (
  {
    getNotificationProps,
    renderContent,
    duration = DEFAULT_DURATION_MS,
    onClose = () => {},
  }: ShowReactiveNotificationOptions,
  appContext: AppContext | null = null,
): NotificationId => {
  mountToaster(appContext);

  return toast.custom(ToastNotification, {
    componentProps: { getNotificationProps, renderContent },
    duration: duration > 0 ? duration : Infinity,
    onAutoClose: onClose,
    onDismiss: onClose,
  });
};

const showNotificationInContext = <C extends Component>(
  options: ShowNotificationOptions<C>,
  appContext: AppContext | null = null,
): NotificationId => {
  const { text, content, contentProps, duration, onClose, ...notificationProps } = options;

  return showReactiveNotification({
    getNotificationProps: () => notificationProps,
    renderContent: () => (content ? h(content, contentProps) : text),
    duration,
    onClose,
  }, appContext);
};

const createShorthands = (appContext: AppContext | null): NotificationShorthands => Object.fromEntries(
  NOTIFICATION_TYPES.map((type) => [
    type,
    (text: string, options: NotificationShorthandOptions = {}) => showNotificationInContext(
      { ...options, type, text },
      appContext,
    ),
  ]),
) as NotificationShorthands;

/**
 * Shows a BaseNotification as a stacked toast and returns its id, e.g.
 *   showNotification.success('Saved')
 *   showNotification.error('Could not save', { duration: 0 })
 *   showNotification({ type: 'success', content: MyToastContent, contentProps: { room } })
 */
export const showNotification = Object.assign(showNotificationInContext, createShorthands(null));

export const dismissNotification = (id: NotificationId): void => {
  toast.dismiss(id);
};

/**
 * Binds the notification functions to the calling component's app context. Call it from setup().
 */
export const useToaster = () => {
  const appContext = getCurrentInstance()?.appContext ?? null;

  return {
    showNotification: Object.assign(
      <C extends Component>(options: ShowNotificationOptions<C>): NotificationId => (
        showNotificationInContext(options, appContext)
      ),
      createShorthands(appContext),
    ),
    showReactiveNotification(options: ShowReactiveNotificationOptions): NotificationId {
      return showReactiveNotification(options, appContext);
    },
    dismissNotification,
  };
};
